import React from 'react';
import {IconName} from '@lightricks/react-design-system';
import {IconProps} from '@lightricks/react-design-system/dist/components/icon/IconProps';
import PERMISSIONS from '@/config/permissions';
import ROLES, {Role} from '@/config/roles';
import {ROUTES} from '@/config/routesConstants';
import {Merchant} from '@/types/agency';
import {RequestedPermission} from '@/types/permissions';
import {Subscription} from '@/types/subscription';

const SCALER_LOGO = '/assets/svg/scaler-logo.svg';

export interface DashboardUserCurrentBrand {
  hasUnreadCollaborations?: boolean;
  hasUnreadConversations?: boolean;
  hasBrandActionItems?: boolean;
}

export interface CustomIsDisplayedParams {
  currentBrand?: Merchant;
  subscription?: Subscription;
  flags?: Record<string, boolean>;
}

/** RDS icon with optional extra props */
export interface MenuItemRdsIcon extends Omit<IconProps, 'appearance'> {
  type: 'icon';
  appearance?: IconProps['appearance'];
}

/** Custom image icon */
export interface MenuItemImageIcon
  extends React.ImgHTMLAttributes<HTMLImageElement> {
  type: 'image';
}

/** Icon descriptor — either an RDS icon or a custom image */
export type MenuItemIcon = MenuItemRdsIcon | MenuItemImageIcon;

export type MenuItem = {
  // Identity
  id: string;
  isSeparator?: boolean;

  // Display
  localeLabelKey: string;
  startIcon: MenuItemIcon;
  endIcon?: MenuItemIcon;

  // Navigation
  link: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
  useEndMatch?: boolean;

  // Visibility & access control
  flagName?: string;
  requiredRoles: Role[];
  requiredPermissionsToDisplay: RequestedPermission[];
  requiredPermissionsToUnlock: RequestedPermission[];
  customIsDisplayedFunc?: (params: CustomIsDisplayedParams) => boolean;

  // Runtime state
  showUnreadIndicatorFunc?: (
    currentBrand?: DashboardUserCurrentBrand
  ) => boolean;

  // Layout
  mobileLocation?: 'main' | 'submenu';
};

// Shared menu items object - defines all possible menu items with their base properties
export const SHARED_MENU_ITEMS: Record<string, Omit<MenuItem, 'id'>> = {
  SEPARATOR: {
    isSeparator: true,
    localeLabelKey: '',
    startIcon: {type: 'icon', name: '' as IconName}, // never rendered
    link: '',
    requiredRoles: [],
    requiredPermissionsToDisplay: [],
    requiredPermissionsToUnlock: [],
  },
  HOME: {
    localeLabelKey: 'Home',
    startIcon: {type: 'icon', name: 'Navigation-Home-Stroke'},
    link: `/`,
    requiredRoles: [],
    requiredPermissionsToDisplay: [],
    requiredPermissionsToUnlock: [],
    mobileLocation: 'submenu',
  },
  CAMPAIGNS: {
    localeLabelKey: 'Campaigns',
    startIcon: {type: 'icon', name: 'Navigation-Megaphone'},
    link: `/campaigns`,
    useEndMatch: false,
    requiredRoles: [],
    requiredPermissionsToDisplay: [],
    requiredPermissionsToUnlock: [PERMISSIONS.CAMPAIGNS.VIEW],
    showUnreadIndicatorFunc: (currentBrand) =>
      !!currentBrand?.hasBrandActionItems,
    mobileLocation: 'main',
  },
  CAMPAIGNS_V2: {
    localeLabelKey: 'Campaigns V2',
    startIcon: {type: 'icon', name: 'Navigation-Megaphone'},
    link: ROUTES.CAMPAIGNS_V2,
    useEndMatch: false,
    flagName: 'reactCampaignsV2',
    requiredRoles: [],
    requiredPermissionsToDisplay: [],
    requiredPermissionsToUnlock: [PERMISSIONS.CAMPAIGNS.VIEW],
    showUnreadIndicatorFunc: (currentBrand) =>
      !!currentBrand?.hasBrandActionItems,
    mobileLocation: 'main',
  },
  SAFE_COLLAB: {
    localeLabelKey: 'Safe Collab',
    startIcon: {type: 'icon', name: 'Navigation-Shopbag-Shield-Stroke'},
    link: `/vetting/hub`,
    requiredRoles: [],
    requiredPermissionsToDisplay: [PERMISSIONS.VETTING.VIEW],
    requiredPermissionsToUnlock: [],
  },
  SEARCH: {
    localeLabelKey: 'Search',
    startIcon: {type: 'icon', name: 'Actions-Search-Generic'},
    link: `/search`,
    requiredRoles: [],
    requiredPermissionsToDisplay: [],
    requiredPermissionsToUnlock: [],
    mobileLocation: 'main',
  },
  SEARCH_LEGACY: {
    localeLabelKey: 'Search (Legacy)',
    startIcon: {type: 'icon', name: 'Actions-Search-Generic'},
    link: `/search/profiles`,
    requiredRoles: [ROLES.PLATFORM_ADMIN],
    requiredPermissionsToDisplay: [],
    requiredPermissionsToUnlock: [],
    mobileLocation: 'main',
  },
  CREATORS: {
    localeLabelKey: 'Lists',
    startIcon: {type: 'icon', name: 'Actions-Bookmark'},
    link: `/creators`,
    requiredRoles: [],
    requiredPermissionsToDisplay: [PERMISSIONS.CREATORS.VIEW],
    requiredPermissionsToUnlock: [PERMISSIONS.CREATORS.VIEW],
    mobileLocation: 'main',
  },
  COLLABORATIONS: {
    localeLabelKey: 'Threads',
    startIcon: {type: 'icon', name: 'Navigation-Messages'},
    link: `/collaborations`,
    useEndMatch: false,
    requiredRoles: [],
    requiredPermissionsToDisplay: [PERMISSIONS.COLLABORATIONS.VIEW],
    requiredPermissionsToUnlock: [PERMISSIONS.COLLABORATIONS.VIEW],
    customIsDisplayedFunc: ({subscription, flags}) =>
      flags?.pricingUpdates || !subscription?.isFree,
    showUnreadIndicatorFunc: (currentBrand) =>
      !!currentBrand?.hasUnreadCollaborations,
    mobileLocation: 'submenu',
  },
  CONTENT_LIBRARY: {
    localeLabelKey: 'Content Library',
    startIcon: {type: 'icon', name: 'Navigation-Photos-Options'},
    link: `/content-library`,
    requiredRoles: [],
    requiredPermissionsToDisplay: [PERMISSIONS.CONTENT_LIBRARY.VIEW],
    requiredPermissionsToUnlock: [PERMISSIONS.CONTENT_LIBRARY.VIEW],
    mobileLocation: 'main',
  },
  STATS: {
    localeLabelKey: 'Stats',
    startIcon: {type: 'icon', name: 'Navigation-Statistics'},
    link: `/stats`,
    useEndMatch: false,
    requiredRoles: [],
    requiredPermissionsToDisplay: [PERMISSIONS.STATS.VIEW],
    requiredPermissionsToUnlock: [PERMISSIONS.STATS.VIEW],
    mobileLocation: 'submenu',
  },
  BRANDED_CONTENT: {
    localeLabelKey: 'Partnership Ads',
    startIcon: {type: 'icon', name: 'Navigation-SelectSecondary'},
    link: `/partnerships`,
    flagName: 'partnershipAdsEnabled',
    requiredRoles: [],
    requiredPermissionsToDisplay: [],
    requiredPermissionsToUnlock: [],
    customIsDisplayedFunc: ({currentBrand}) =>
      currentBrand?.brandInstagramBusinessAccount?.state === 'active',
    mobileLocation: 'main',
  },
  CREATOR_CRM: {
    localeLabelKey: 'Creators',
    startIcon: {type: 'icon', name: 'Actions-Search-Generic'},
    link: `/creator-crm`,
    requiredRoles: [],
    requiredPermissionsToDisplay: [],
    requiredPermissionsToUnlock: [],
    mobileLocation: 'main',
  },
  RECRUIT_CRM: {
    localeLabelKey: 'CRM Recruit Tracking',
    startIcon: {type: 'icon', name: 'Actions-Search-Person'},
    link: ROUTES.RECRUIT_CRM,
    requiredRoles: [ROLES.PLATFORM_ADMIN],
    requiredPermissionsToDisplay: [],
    requiredPermissionsToUnlock: [],
    mobileLocation: 'main',
  },
  CONTENT_LAB: {
    localeLabelKey: 'Ltx Scaler',
    startIcon: {type: 'image', src: SCALER_LOGO, width: 20, height: 20},
    endIcon: {type: 'icon', name: 'Actions-Arrow-Long-Forward'},
    link: `/content-lab`,
    target: '_blank',
    flagName: 'contentLab',
    requiredRoles: [],
    requiredPermissionsToDisplay: [PERMISSIONS.CONTENT_LAB.VIEW],
    requiredPermissionsToUnlock: [],
    mobileLocation: 'main',
  },
};

// Menu items for FIFA Revamp feature flag
export const MENU_ITEMS__FIFA_REVAMP: MenuItem[] = [
  {id: 'home', ...SHARED_MENU_ITEMS.HOME, localeLabelKey: 'Home'},
  {
    id: 'creator-crm',
    ...SHARED_MENU_ITEMS.CREATOR_CRM,
  },
  {id: 'creators', ...SHARED_MENU_ITEMS.CREATORS},
  {
    id: 'safe-collab',
    ...SHARED_MENU_ITEMS.SAFE_COLLAB,
  },
  {
    id: 'campaigns',
    ...SHARED_MENU_ITEMS.CAMPAIGNS,
  },
  {
    id: 'campaigns-v2',
    ...SHARED_MENU_ITEMS.CAMPAIGNS_V2,
  },
  {id: 'separator-1', ...SHARED_MENU_ITEMS.SEPARATOR},
  {
    id: 'recruit-crm',
    ...SHARED_MENU_ITEMS.RECRUIT_CRM,
  },
  {
    id: 'collaborations',
    ...SHARED_MENU_ITEMS.COLLABORATIONS,
    requiredRoles: [ROLES.PLATFORM_ADMIN],
  },
  {
    id: 'search',
    ...SHARED_MENU_ITEMS.SEARCH,
    requiredRoles: [ROLES.PLATFORM_ADMIN],
  },
  {
    id: 'content-library',
    ...SHARED_MENU_ITEMS.CONTENT_LIBRARY,
    requiredRoles: [ROLES.PLATFORM_ADMIN],
  },
  {
    id: 'stats',
    ...SHARED_MENU_ITEMS.STATS,
    requiredRoles: [ROLES.PLATFORM_ADMIN],
  },
  {
    id: 'branded-content',
    ...SHARED_MENU_ITEMS.BRANDED_CONTENT,
    requiredRoles: [ROLES.PLATFORM_ADMIN],
  },
];

// Standard menu items (non-FIFA)
export const MENU_ITEMS__BASIC: MenuItem[] = [
  {id: 'home', ...SHARED_MENU_ITEMS.HOME},
  {id: 'campaigns', ...SHARED_MENU_ITEMS.CAMPAIGNS},
  {id: 'campaigns-v2', ...SHARED_MENU_ITEMS.CAMPAIGNS_V2},
  {id: 'safe-collab', ...SHARED_MENU_ITEMS.SAFE_COLLAB},
  {id: 'search', ...SHARED_MENU_ITEMS.SEARCH},
  {id: 'search-legacy', ...SHARED_MENU_ITEMS.SEARCH_LEGACY},
  {id: 'creators', ...SHARED_MENU_ITEMS.CREATORS},
  {id: 'collaborations', ...SHARED_MENU_ITEMS.COLLABORATIONS},
  {id: 'content-library', ...SHARED_MENU_ITEMS.CONTENT_LIBRARY},
  {id: 'stats', ...SHARED_MENU_ITEMS.STATS},
  {id: 'branded-content', ...SHARED_MENU_ITEMS.BRANDED_CONTENT},
  {id: 'separator-1', ...SHARED_MENU_ITEMS.SEPARATOR},
  {id: 'content-lab', ...SHARED_MENU_ITEMS.CONTENT_LAB},
];

/** Items shown in the UserMenu hamburger popover on mobile */
export const MOBILE_SUB_MENU_ITEMS: MenuItem[] = MENU_ITEMS__BASIC.filter(
  (item) => item.mobileLocation === 'submenu'
);
